'use client';

import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Plus, 
  Activity, 
  Pill, 
  Calendar, 
  TrendingUp, 
  FileText,
  ChevronRight,
  Clock,
  AlertCircle,
  CheckCircle2,
  Heart,
  Brain,
  Moon,
  Utensils
} from 'lucide-react';
import { useHealthLogs } from '@/features/health/hooks/useHealthLogs';
import { useHealthStats } from '@/features/health/hooks/useHealthStats';
import { useMedications } from '@/features/medications/hooks/useMedications';
import { useChildren } from '@/features/children/hooks/useChildren';
import { HealthOverviewWidget } from '@/features/health/components/widgets/HealthOverviewWidget';
import { QuickLogWidget } from '@/features/health/components/widgets/QuickLogWidget';
import { RecentActivityWidget } from '@/features/health/components/widgets/RecentActivityWidget';
import { MedicationReminderWidget } from '@/features/health/components/widgets/MedicationReminderWidget';
import { HealthInsightsWidget } from '@/features/health/components/widgets/HealthInsightsWidget';
import { ChildSelector } from '@/components/shared/ChildSelector';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { PageHeader } from '@/components/layout/PageHeader';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatRelativeTime } from '@/utils/dateUtils';
import type { Child } from '@/features/family/types/familyTypes';

interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  logType: string;
}

const quickActions: QuickAction[] = [
  { 
    id: 'seizure', 
    label: 'Log Seizure', 
    icon: <Brain className="w-5 h-5" />, 
    color: 'text-red-500 bg-red-50',
    logType: 'seizure'
  },
  { 
    id: 'medication', 
    label: 'Log Medication', 
    icon: <Pill className="w-5 h-5" />, 
    color: 'text-blue-500 bg-blue-50',
    logType: 'medication'
  },
  { 
    id: 'symptom', 
    label: 'Log Symptom', 
    icon: <Heart className="w-5 h-5" />, 
    color: 'text-terracotta bg-terracotta/10',
    logType: 'symptom'
  },
  { 
    id: 'sleep', 
    label: 'Log Sleep', 
    icon: <Moon className="w-5 h-5" />, 
    color: 'text-purple-500 bg-purple-50',
    logType: 'sleep'
  },
  { 
    id: 'feeding', 
    label: 'Log Feeding', 
    icon: <Utensils className="w-5 h-5" />, 
    color: 'text-green-500 bg-green-50',
    logType: 'feeding'
  },
  { 
    id: 'therapy', 
    label: 'Log Therapy', 
    icon: <Activity className="w-5 h-5" />, 
    color: 'text-sage bg-sage/10',
    logType: 'therapy'
  },
];

export function HealthDashboardPage(): React.ReactElement {
  const navigate = useNavigate();
  const { familyId } = useParams<{ familyId: string }>();
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [showQuickLog, setShowQuickLog] = useState(false);
  const [quickLogType, setQuickLogType] = useState<string | null>(null);

  // Fetch children for the family
  const { 
    data: children, 
    isLoading: childrenLoading 
  } = useChildren(familyId!);

  // Auto-select first child if none selected
  React.useEffect(() => {
    if (children && children.length > 0 && !selectedChildId) {
      setSelectedChildId(children[0].id);
    }
  }, [children, selectedChildId]);

  // Fetch health data for selected child
  const { 
    data: healthLogsData, 
    isLoading: logsLoading 
  } = useHealthLogs(selectedChildId || '', {
    limit: 10,
    sortBy: 'occurred_at',
    sortOrder: 'desc',
  });

  const { 
    data: healthStats, 
    isLoading: statsLoading 
  } = useHealthStats(selectedChildId || '', {
    period: '30d',
  });

  const { 
    data: medications, 
    isLoading: medicationsLoading 
  } = useMedications(selectedChildId || '', {
    activeOnly: true,
  });

  const isLoading = childrenLoading || logsLoading || statsLoading || medicationsLoading;

  const selectedChild = useMemo(() => {
    return children?.find((c: Child) => c.id === selectedChildId);
  }, [children, selectedChildId]);

  const handleQuickAction = (action: QuickAction) => {
    setQuickLogType(action.logType);
    setShowQuickLog(true);
  };

  const handleQuickLogClose = () => {
    setShowQuickLog(false);
    setQuickLogType(null);
  };

  const handleViewAllLogs = () => {
    navigate(`/family/${familyId}/health/logs${selectedChildId ? `?childId=${selectedChildId}` : ''}`);
  };

  const handleViewMedications = () => {
    navigate(`/family/${familyId}/health/medications${selectedChildId ? `?childId=${selectedChildId}` : ''}`);
  };

  const handleViewReports = () => {
    navigate(`/family/${familyId}/health/reports${selectedChildId ? `?childId=${selectedChildId}` : ''}`);
  };

  const handleNewLog = () => {
    navigate(`/family/${familyId}/health/logs/new${selectedChildId ? `?childId=${selectedChildId}` : ''}`);
  };

  if (childrenLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!children || children.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <EmptyState
          icon={<Activity className="w-12 h-12 text-warm-gray" />}
          title="No children added yet"
          description="Add a child to your family to start tracking their health."
          action={
            <Button onClick={() => navigate(`/family/${familyId}/children/new`)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Child
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Page Header */}
      <PageHeader
        title="Health Tracking"
        description={selectedChild ? `Tracking health for ${selectedChild.first_name}` : 'Track and monitor health'}
        actions={
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={handleViewReports}>
              <FileText className="w-4 h-4 mr-2" />
              Reports
            </Button>
            <Button onClick={handleNewLog}>
              <Plus className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
          </div>
        }
      />

      {/* Child Selector */}
      {children.length > 1 && (
        <div className="mb-6">
          <ChildSelector
            children={children}
            selectedChildId={selectedChildId}
            onSelect={setSelectedChildId}
          />
        </div>
      )}

      {/* Quick Actions */}
      <section className="mb-8">
        <h2 className="text-lg font-display font-semibold text-navy mb-4">
          Quick Log
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={() => handleQuickAction(action)}
              className={`
                flex flex-col items-center justify-center p-4 rounded-xl
                border border-transparent hover:border-sage/30
                transition-all duration-200 hover:shadow-soft
                ${action.color}
              `}
            >
              {action.icon}
              <span className="mt-2 text-sm font-medium text-navy">
                {action.label}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* Main Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Overview & Activity */}
        <div className="lg:col-span-2 space-y-6">
          {/* Health Overview */}
          <HealthOverviewWidget
            stats={healthStats}
            isLoading={statsLoading}
            childName={selectedChild?.first_name}
          />

          {/* Recent Activity */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-navy">
                Recent Activity
              </h3>
              <button
                onClick={handleViewAllLogs}
                className="text-sm text-sage hover:text-sage-dark font-medium flex items-center"
              >
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
            <RecentActivityWidget
              logs={healthLogsData?.items || []}
              isLoading={logsLoading}
            />
          </Card>

          {/* Health Insights */}
          <HealthInsightsWidget
            stats={healthStats}
            isLoading={statsLoading}
          />
        </div>

        {/* Right Column - Medications & Calendar */}
        <div className="space-y-6">
          {/* Medication Reminders */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-navy flex items-center">
                <Pill className="w-5 h-5 mr-2 text-sage" />
                Medications
              </h3>
              <button
                onClick={handleViewMedications}
                className="text-sm text-sage hover:text-sage-dark font-medium flex items-center"
              >
                Manage
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
            <MedicationReminderWidget
              medications={medications || []}
              isLoading={medicationsLoading}
            />
          </Card>

          {/* Upcoming Appointments Teaser */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-navy flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-sage" />
                Upcoming
              </h3>
              <button
                onClick={() => navigate(`/family/${familyId}/calendar`)}
                className="text-sm text-sage hover:text-sage-dark font-medium flex items-center"
              >
                Calendar
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
            <div className="text-center py-8 text-warm-gray">
              <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No upcoming appointments</p>
              <button
                onClick={() => navigate(`/family/${familyId}/calendar/new`)}
                className="mt-2 text-sm text-sage hover:text-sage-dark font-medium"
              >
                Schedule Appointment
              </button>
            </div>
          </Card>

          {/* Quick Stats */}
          <Card className="p-6 bg-gradient-to-br from-sage/10 to-sage/5">
            <h3 className="font-display font-semibold text-navy mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-sage" />
              30-Day Summary
            </h3>
            {statsLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner size="sm" />
              </div>
            ) : healthStats ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-warm-gray">Total Entries</span>
                  <span className="font-semibold text-navy">
                    {healthStats.totalLogs || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-warm-gray">Seizure-Free Days</span>
                  <span className="font-semibold text-green-600">
                    {healthStats.seizureFreeDays || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-warm-gray">Therapy Sessions</span>
                  <span className="font-semibold text-navy">
                    {healthStats.therapySessions || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-warm-gray">Med Adherence</span>
                  <span className="font-semibold text-navy">
                    {healthStats.medicationAdherence || 0}%
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-warm-gray text-center py-4">
                No data available
              </p>
            )}
          </Card>
        </div>
      </div>

      {/* Quick Log Modal */}
      {showQuickLog && selectedChildId && (
        <QuickLogWidget
          childId={selectedChildId}
          logType={quickLogType}
          onClose={handleQuickLogClose}
          onSuccess={handleQuickLogClose}
        />
      )}
    </div>
  );
}

export default HealthDashboardPage;

